import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { useChannels } from "@/lib/feature-hooks";
import { toast } from "sonner";
import { Plus, TrendingUp, Trash2 } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function StatsChannelsPanel() {
  const { active } = useServer();
  const channels = useChannels(active?.id, active?.bot_present);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ channel_id: "", template: "📊 Members: {count}", metric: "members" });

  const refresh = () => {
    if (!active?.id || !active.bot_present) return;
    api.get(`/features/stats-channels/${active.id}`).then((r) => setList(r.data));
  };
  useEffect(refresh, [active?.id, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;

  const create = async (e) => {
    e.preventDefault();
    if (!form.channel_id) return;
    try {
      await api.post(`/features/stats-channels/${active.id}`, form);
      toast.success("Stat channel created");
      setForm({ channel_id: "", template: "📊 Members: {count}", metric: "members" });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/features/stats-channels/${active.id}/${id}`);
    refresh();
  };

  return (
    <div data-testid="stats-channels-panel" className="space-y-8">
      <div>
        <div className="overline mb-2"><TrendingUp className="inline w-4 h-4 mr-2 -mt-1" />UTILITIES</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Statistics Channels</h1>
        <p className="text-zinc-400 mt-2">Auto-renamed channels showing live stats (member count, etc).</p>
      </div>

      <form onSubmit={create} className="tactical-card p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="sc-form">
        <select className="tactical-input" value={form.channel_id} onChange={(e) => setForm({ ...form, channel_id: e.target.value })} data-testid="sc-channel">
          <option value="">— Select channel —</option>
          {channels.map((c) => <option key={c.id} value={c.id}>{c.type === "voice" ? "🔊" : "#"}{c.name}</option>)}
        </select>
        <input className="tactical-input" placeholder="📊 Members: {count}" value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })} data-testid="sc-template" />
        <button type="submit" className="btn-primary justify-center" data-testid="sc-create-btn"><Plus className="w-4 h-4" /> Create</button>
      </form>

      <div className="tactical-card p-6">
        <h2 className="font-heading uppercase text-lg mb-4">Active ({list.length})</h2>
        <div className="space-y-2">
          {list.map((s) => (
            <div key={s.id} className="border border-brand-line p-3 rounded-lg flex items-center gap-3" data-testid={`sc-row-${s.id}`}>
              <TrendingUp className="w-4 h-4 text-brand-orange" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-mono-tabular truncate">{s.template}</div>
                <div className="text-xs text-zinc-500">Channel: {s.channel_id} · Last value: {s.last_value}</div>
              </div>
              <button onClick={() => remove(s.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
